import { devError } from '../utils/devLogger';
import React, { useState, useRef } from 'react';
import { Button, Card, Form, ProgressBar, Alert, Modal, Tab, Tabs, Badge, InputGroup } from 'react-bootstrap';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import {
  MAX_CONTENT_FILE_SIZES,
  CONTENT_ACCEPTED_FILE_TYPES,
  CONTENT_FILE_INPUT_ACCEPT
} from '../constants/fileUploadLimits';

const ContentUpload = ({ moduleId, onUploadComplete }) => {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadType, setUploadType] = useState('document');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [contentMetadata, setContentMetadata] = useState({
    title: '',
    description: '',
    tags: [],
    category: 'lesson',
    accessLevel: 'module',
    duration: '',
    fileType: '',
    releaseAt: ''
  });
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const fileSize = file.size;
    const maxSize = MAX_CONTENT_FILE_SIZES[uploadType];
    
    if (fileSize > maxSize) {
      throw new Error(`File size exceeds maximum limit of ${(maxSize / (1024 * 1024)).toFixed(0)}MB for ${uploadType} files`);
    }

    const fileName = file.name.toLowerCase();
    const acceptedTypes = CONTENT_ACCEPTED_FILE_TYPES[uploadType].split(',');
    const isValidType = acceptedTypes.some(type => fileName.endsWith(type.replace('.', '')));
    
    if (!isValidType) {
      throw new Error(`Invalid file type. Accepted types for ${uploadType}: ${CONTENT_ACCEPTED_FILE_TYPES[uploadType]}`);
    }

    return true;
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('video')) return '🎥';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('audio')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('document') || fileType.includes('word')) return '📝';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📊';
    return '📁';
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      validateFile(file);
      
      // Auto-fill metadata based on file
      setContentMetadata(prev => ({
        ...prev,
        title: prev.title || file.name.split('.')[0],
        fileType: file.type,
        duration: uploadType === 'video' ? 'To be determined' : ''
      }));

      // Show preview for supported file types
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewContent({ type: 'image', src: e.target.result });
          setShowPreview(true);
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      alert(error.message);
      event.target.value = '';
    }
  };

  const uploadFile = async (file) => {
    if (!moduleId || !currentUser) {
      throw new Error('Module ID and user authentication required');
    }

    const timestamp = new Date().getTime();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `lms-content/${uploadType}/${moduleId}/${timestamp}_${sanitizedFileName}`;
    
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          devError('Upload error:', error);
          reject(error);
        },
        () => {
          resolve({
            path: storagePath,
            size: file.size,
            type: file.type
          });
        }
      );
    });
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file) {
      alert('Please select a file to upload');
      return;
    }

    if (!contentMetadata.title.trim()) {
      alert('Please enter a title for the content');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Upload file to Firebase Storage
      const fileData = await uploadFile(file);

      // Create content record
      const contentRecord = {
        id: `content_${Date.now()}`,
        title: contentMetadata.title,
        description: contentMetadata.description,
        type: uploadType,
        category: contentMetadata.category,
        accessLevel: contentMetadata.accessLevel,
        releaseAt: contentMetadata.releaseAt ? new Date(contentMetadata.releaseAt) : null,
        file: {
          path: fileData.path,
          size: fileData.size,
          type: fileData.type,
          filename: file.name
        },
        metadata: {
          duration: contentMetadata.duration,
          tags: contentMetadata.tags,
          uploadedBy: currentUser.uid,
          uploadedAt: new Date().toISOString()
        }
      };

      // Update module with new content
      const moduleRef = doc(db, 'modules', moduleId);
      await updateDoc(moduleRef, {
        content: arrayUnion(contentRecord),
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      });

      setUploadedFiles(prev => [...prev, contentRecord]);
      
      // Reset form
      setContentMetadata({
        title: '',
        description: '',
        tags: [],
        category: 'lesson',
        accessLevel: 'module',
        duration: '',
        fileType: '',
        releaseAt: ''
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (onUploadComplete) {
        onUploadComplete(contentRecord);
      }

      alert('Content uploaded successfully!');
    } catch (error) {
      devError('Upload failed:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const addTag = (tag) => {
    if (tag.trim() && !contentMetadata.tags.includes(tag.trim())) {
      setContentMetadata(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setContentMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <div className="content-upload">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">📤 Upload Learning Content</h5>
            <Badge bg="info">Module: {moduleId}</Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Tabs activeKey={uploadType} onSelect={setUploadType} className="mb-4">
            <Tab eventKey="video" title="🎥 Video">
              <Alert variant="info" className="small">
                <strong>Video Guidelines:</strong> Max 500MB. Supported formats: MP4, MOV, AVI, WMV, FLV, WebM, MKV
              </Alert>
            </Tab>
            <Tab eventKey="document" title="📄 Document">
              <Alert variant="info" className="small">
                <strong>Document Guidelines:</strong> Max 50MB. Supported formats: PDF, DOC, DOCX, PPT, PPTX, TXT, MD, RTF
              </Alert>
            </Tab>
            <Tab eventKey="image" title="🖼️ Image">
              <Alert variant="info" className="small">
                <strong>Image Guidelines:</strong> Max 10MB. Supported formats: JPG, JPEG, PNG, GIF, BMP, SVG, WebP
              </Alert>
            </Tab>
            <Tab eventKey="audio" title="🎵 Audio">
              <Alert variant="info" className="small">
                <strong>Audio Guidelines:</strong> Max 100MB. Supported formats: MP3, WAV, FLAC, AAC, OGG, M4A
              </Alert>
            </Tab>
          </Tabs>

          <Form>
            <div className="row">
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label>Content Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={contentMetadata.title}
                    onChange={(e) => setContentMetadata(prev => ({...prev, title: e.target.value}))}
                    placeholder="Enter a descriptive title"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={contentMetadata.category}
                    onChange={(e) => setContentMetadata(prev => ({...prev, category: e.target.value}))}
                  >
                    <option value="lesson">Lesson Content</option>
                    <option value="assignment">Assignment Material</option>
                    <option value="reference">Reference Material</option>
                    <option value="exercise">Exercise/Activity</option>
                    <option value="assessment">Assessment</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={contentMetadata.description}
                onChange={(e) => setContentMetadata(prev => ({...prev, description: e.target.value}))}
                placeholder="Describe what learners will gain from this content"
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>File Selection</Form.Label>
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept={CONTENT_FILE_INPUT_ACCEPT[uploadType]}
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Access Level</Form.Label>
                  <Form.Select
                    value={contentMetadata.accessLevel}
                    onChange={(e) => setContentMetadata(prev => ({...prev, accessLevel: e.target.value}))}
                  >
                    <option value="module">Module Members</option>
                    <option value="cohort">Cohort Members</option>
                    <option value="program">Program Members</option>
                    <option value="public">All Learners</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Release Date (optional)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={contentMetadata.releaseAt}
                    onChange={(e) => setContentMetadata(prev => ({...prev, releaseAt: e.target.value}))}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <div className="mb-2">
                {contentMetadata.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    bg="secondary"
                    className="me-1 mb-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => removeTag(tag)}
                  >
                    {tag} ×
                  </Badge>
                ))}
              </div>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Add tags (press Enter)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  variant="outline-secondary"
                  onClick={(e) => {
                    const input = e.target.parentElement.querySelector('input');
                    addTag(input.value);
                    input.value = '';
                  }}
                >
                  Add Tag
                </Button>
              </InputGroup>
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">Uploading...</small>
                  <small className="text-muted">{uploadProgress}%</small>
                </div>
                <ProgressBar now={uploadProgress} animated striped />
              </div>
            )}

            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={uploading || !contentMetadata.title.trim()}
              >
                {uploading ? '⏳ Uploading...' : '📤 Upload Content'}
              </Button>
            </div>
          </Form>

          {uploadedFiles.length > 0 && (
            <div className="mt-4">
              <h6>Recently Uploaded</h6>
              <div className="list-group">
                {uploadedFiles.slice(-3).map((file, index) => (
                  <div key={index} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">
                          {getFileIcon(file.file.type)} {file.title}
                        </h6>
                        <p className="mb-1 text-muted small">{file.description}</p>
                        <small className="text-success">✅ Upload completed</small>
                      </div>
                      <Badge bg="primary">{file.type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Content Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewContent?.type === 'image' && (
            <img
              src={previewContent.src}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close Preview
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContentUpload;
